"use client"

import { useEffect, useRef, useState } from "react"
import { Button } from "@/components/ui/button"
import { X, Volume2, VolumeX } from "@/lib/icons"
import { parseVAST, fireTrackingUrls, type VASTResult } from "@/lib/vast-parser"
import { trackZoneImpression, trackZoneClick } from "@/lib/ad-zone-tracking"
import { cn } from "@/lib/utils"
import { useTranslations } from "next-intl"

interface VideoPrerollProps {
  vastUrl?: string           // VAST URL from network — parsed automatically
  fallbackVideoUrl?: string  // Direct video URL if no VAST or VAST fails (no hardcoded default)
  skipAfterSecs?: number     // Override skip timing (0 = non-skippable)
  maxDurationSecs?: number   // Max ad duration
  /** Already-resolved VAST (from ad-preroll-cache's background warm) — skips
   * the fetch+parse round trip entirely so the ad is ready instantly instead
   * of flashing "Loading ad..." while it resolves. `result: null` means the
   * warm already confirmed no fill; still skips straight to the no-fill path. */
  preresolved?: { vastUrl: string; result: VASTResult | null }
  /** ad_zones UUID. The VAST tracking above reports to the NETWORK; this is
   *  our own record, which preroll previously had none of — video showed as
   *  zero impressions in Goonru's reporting even when ads played fine. */
  zoneId?: string | null
  /**
   * Play the ad inside the video's own frame (an absolutely-positioned
   * overlay filling its nearest positioned ancestor) instead of taking over
   * the whole screen.
   *
   * This is what a pre-roll is actually supposed to be: on YouTube and every
   * other player, the ad plays in the player, and the page around it stays
   * put. The original `fixed inset-0` behaviour blacked out the entire app —
   * which is bad on its own, and genuinely hostile combined with the
   * non-skippable option (admin_settings.video_ads_config.allow_non_skippable,
   * up to max_non_skippable_duration seconds): the whole UI was held hostage
   * with no exit, not just the video.
   */
  inline?: boolean
  onComplete: () => void
  onSkip?: () => void
}

export function VideoPreroll({
  vastUrl,
  fallbackVideoUrl,
  skipAfterSecs,
  maxDurationSecs = 30,
  preresolved,
  zoneId,
  inline = false,
  onComplete,
  onSkip,
}: VideoPrerollProps) {
  // Inline fills the video frame it's mounted in; the legacy full-screen mode
  // sits above everything (z-200 clears the modal at z-50).
  //
  // z-10, not z-40: the media container it's mounted in (`relative`, no
  // z-index of its own) doesn't form a stacking context, so this competes
  // directly against its SIBLINGS in booru-detail-modal.tsx — the per-post
  // toolbar (z-20) and the modal's own close button (z-30) — not just
  // things layered inside the container. At z-40 this correctly stayed
  // bounded to the media area exactly as intended (verified: the shell's own
  // rect matched the container, not the viewport) but still outranked and
  // painted an opaque black layer straight over the toolbar and close
  // button, which is indistinguishable from the old full-screen takeover to
  // actually look at — nothing above the video was visible either way. z-10
  // keeps it under both, so the frame around the ad — and the way out —
  // stays visible and usable while it plays, the way an in-stream ad on any
  // other video site behaves.
  const shellClass = inline ? "absolute inset-0 z-10 bg-black" : "fixed inset-0 z-[200] bg-black"
  const t = useTranslations("videoPreroll")
  const videoRef = useRef<HTMLVideoElement>(null)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const initialMediaFile = preresolved?.result?.mediaFiles.length
    ? (preresolved.result.mediaFiles.find((m) => m.type === "video/mp4") ?? preresolved.result.mediaFiles[0])
    : null

  const [vast, setVast] = useState<VASTResult | null>(preresolved ? preresolved.result : null)
  const [videoUrl, setVideoUrl] = useState<string | null>(initialMediaFile?.url ?? null)
  const [loading, setLoading] = useState(preresolved ? false : !!vastUrl)
  const [elapsed, setElapsed] = useState(0)
  // Admin's muted_by_default (VideoAdsManager) has a real, working toggle but
  // was never actually read on this serving path — same class of bug as the
  // letterbox/pillarbox frequency settings had (see the comment on the
  // letterbox fetch in booru-detail-modal.tsx): the admin UI worked, nothing
  // downstream consumed it, so it silently did nothing. Starts muted (safe,
  // matches old hardcoded behavior) until the real config value loads.
  const [muted, setMuted] = useState(true)
  const [started, setStarted] = useState(false)

  useEffect(() => {
    fetch("/api/admin/video-ads-config")
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        const mutedByDefault = d?.config?.muted_by_default
        if (mutedByDefault === false) setMuted(false)
      })
      .catch(() => {})
  }, [])

  // Resolve skip timing: VAST skipOffset → prop override → default 5s
  const effectiveSkip = skipAfterSecs !== undefined
    ? skipAfterSecs
    : (vast?.skipOffset ?? 5)
  const canSkip = effectiveSkip === 0 || elapsed >= effectiveSkip
  const duration = Math.min(vast?.duration ?? maxDurationSecs, maxDurationSecs)

  // A background-warmed ad (see lib/ad-preroll-cache.ts) already has its
  // result — no fetch/parse needed. Only the no-media-file case (confirmed
  // no fill, or a fill with no usable MediaFile) needs handling here, since
  // the state initializers above already applied a real result.
  useEffect(() => {
    if (!preresolved) return
    if (!initialMediaFile) {
      if (fallbackVideoUrl) setVideoUrl(fallbackVideoUrl)
      else onComplete()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Parse VAST on mount; if no vastUrl and no fallback, skip immediately
  useEffect(() => {
    if (preresolved) return
    if (!vastUrl) {
      if (fallbackVideoUrl) {
        setVideoUrl(fallbackVideoUrl)
      } else {
        // No ad to show — dismiss immediately
        onComplete()
      }
      setLoading(false)
      return
    }

    let settled = false

    // parseVAST can follow up to 3 wrapper-redirect hops, each with its own
    // 5s server-side timeout (see app/api/ads/vast-proxy/route.ts) — with no
    // cap on the whole chain, a real multi-hop wrapper (common for ad
    // networks routing through an RTB layer) could leave this on "Loading
    // ad..." for 15+ seconds with zero feedback, which is exactly what a
    // real user reported as the ad being permanently stuck. Race the whole
    // resolution against a single timeout so it always gives up and shows
    // the actual video instead of hanging indefinitely.
    const timeout = new Promise<null>((resolve) => setTimeout(() => resolve(null), 6000))

    Promise.race([parseVAST(vastUrl), timeout]).then((result) => {
      if (settled) return
      settled = true
      if (result && result.mediaFiles.length > 0) {
        setVast(result)
        // Pick best media file — prefer mp4, highest bitrate
        const mp4 = result.mediaFiles.find((m) => m.type === "video/mp4") ?? result.mediaFiles[0]
        setVideoUrl(mp4.url)
      } else if (fallbackVideoUrl) {
        // VAST failed or timed out — use explicit fallback if provided
        setVideoUrl(fallbackVideoUrl)
      } else {
        // VAST failed or timed out, no fallback — dismiss rather than hang
        onComplete()
      }
      setLoading(false)
    })

    return () => { settled = true }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vastUrl, fallbackVideoUrl])

  // Start playback once we have a URL
  useEffect(() => {
    if (!videoUrl || loading) return
    const video = videoRef.current
    if (!video) return

    // Setting `volume` doesn't come from the `muted` prop — an unmuted
    // preroll would otherwise jump straight to whatever volume the element
    // defaults to (100%, jarring right after a muted grid). Requested
    // default when audible: on, but not full blast.
    video.volume = 0.5

    video.play().catch(() => {
      video.muted = true
      video.play().catch(() => {})
    })
  }, [videoUrl, loading])

  // A video URL that never plays — blocked by an ad-blocker/privacy
  // extension, a dead CDN link, or a network stall that never fires a clean
  // error event — leaves the ad on a black frame forever with no path back
  // to the actual content, which reads identically to "the ad doesn't play,
  // at all." Neither onError nor onEnded fires for a request that just never
  // resolves, so this needs its own watchdog independent of video events.
  useEffect(() => {
    if (!videoUrl || loading || started) return
    const watchdog = setTimeout(() => {
      if (!started) handleVideoFailure()
    }, 8000)
    return () => clearTimeout(watchdog)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoUrl, loading, started])

  function handleVideoFailure() {
    if (intervalRef.current) clearInterval(intervalRef.current)
    // Already tried the fallback (or this was the fallback) — nothing left to try.
    if (fallbackVideoUrl && videoUrl !== fallbackVideoUrl) {
      setVideoUrl(fallbackVideoUrl)
    } else {
      onComplete()
    }
  }

  // Timer
  useEffect(() => {
    if (loading || !videoUrl) return

    intervalRef.current = setInterval(() => {
      setElapsed((e) => e + 1)
    }, 1000)

    return () => { if (intervalRef.current) clearInterval(intervalRef.current) }
  }, [loading, videoUrl])

  // handleComplete calls onComplete, which in every caller (e.g.
  // SkipAdsModal) updates that PARENT component's own state. Calling it
  // directly from inside the setElapsed functional updater above (as this
  // used to) runs it during React's render of a different component, which
  // React disallows ("Cannot update a component while rendering a different
  // component") — split into its own effect that reacts to elapsed instead.
  const completedRef = useRef(false)
  useEffect(() => {
    if (loading || !videoUrl || completedRef.current) return
    if (elapsed >= duration) {
      completedRef.current = true
      if (intervalRef.current) clearInterval(intervalRef.current)
      handleComplete()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elapsed, duration, loading, videoUrl])

  function handleStart() {
    if (started) return
    setStarted(true)
    // Fire impression + start tracking
    if (vast) {
      fireTrackingUrls(vast.tracking.impression)
      fireTrackingUrls(vast.tracking.start)
    }
    // Our own zone-level record, separate from the network's pixels above.
    // Fired on actual playback start rather than mount, so a preroll that
    // never plays isn't counted.
    trackZoneImpression({ zoneId })
  }

  function handleTimeUpdate() {
    if (!vast || !videoRef.current) return
    const pct = videoRef.current.currentTime / videoRef.current.duration
    // Fire quartile events
    if (pct >= 0.25 && pct < 0.26) fireTrackingUrls(vast.tracking.firstQuartile)
    if (pct >= 0.50 && pct < 0.51) fireTrackingUrls(vast.tracking.midpoint)
    if (pct >= 0.75 && pct < 0.76) fireTrackingUrls(vast.tracking.thirdQuartile)
  }

  function handleComplete() {
    if (vast) fireTrackingUrls(vast.tracking.complete)
    onComplete()
  }

  function handleSkip() {
    if (!canSkip) return
    if (intervalRef.current) clearInterval(intervalRef.current)
    if (vast) fireTrackingUrls(vast.tracking.skip)
    onSkip?.()
    onComplete()
  }

  function handleClick() {
    if (vast?.tracking.clickThrough) {
      fireTrackingUrls(vast.tracking.clickTracking)
      // Our own record alongside the network's clickTracking pixels.
      trackZoneClick({ zoneId })
      window.open(vast.tracking.clickThrough, "_blank", "noopener")
    }
  }

  // Whole seconds only — elapsed is a float from the video clock, and printing it
  // raw showed things like "1.3350000000000009s".
  const remaining = Math.ceil(Math.max(0, duration - elapsed))
  const skipCountdown = Math.ceil(Math.max(0, effectiveSkip - elapsed))

  if (loading) {
    return (
      <div className={cn(shellClass, "flex items-center justify-center")}>
        <div className="text-white/60 text-sm animate-pulse">{t("loadingAd")}</div>
      </div>
    )
  }

  return (
    <div className={cn(shellClass, "flex flex-col items-center justify-center")}>
      {/* Ad label */}
      <div className="absolute top-3 left-3 bg-black/70 text-white text-xs px-2 py-1 rounded select-none">
        {t("advertisement")}
      </div>

      {/* Video */}
      {videoUrl && (
        <video
          ref={videoRef}
          src={videoUrl}
          className="w-full max-w-3xl max-h-[70vh] object-contain cursor-pointer"
          muted={muted}
          playsInline
          onPlay={handleStart}
          onTimeUpdate={handleTimeUpdate}
          onEnded={handleComplete}
          onClick={handleClick}
          onError={handleVideoFailure}
        />
      )}

      {/* Controls */}
      <div className="absolute bottom-4 right-4 z-10 flex items-center gap-3">
        {/* Mute */}
        <button
          onClick={() => {
            const newMuted = !muted
            setMuted(newMuted)
            if (videoRef.current) videoRef.current.muted = newMuted
          }}
          className="bg-black/70 text-white p-2 rounded-full hover:bg-black/90 transition-colors"
          aria-label={muted ? t("unmute") : t("mute")}
        >
          {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
        </button>

        {/* Countdown */}
        <div className="bg-black/70 text-white text-xs px-3 py-1.5 rounded-full select-none">
          {t("remainingS", { remaining })}
        </div>

        {/* Skip */}
        {effectiveSkip === 0 ? (
          <div className="bg-black/70 text-white/60 text-xs px-3 py-1.5 rounded select-none">
            {t("nonSkippable")}
          </div>
        ) : canSkip ? (
          <Button size="sm" variant="secondary" onClick={handleSkip}
            className="bg-white/90 text-black hover:bg-white text-xs">
            <X className="h-3.5 w-3.5 mr-1" />{t("skipAd")}
          </Button>
        ) : (
          <div className="bg-black/70 text-white text-xs px-3 py-1.5 rounded select-none">
            {t("skipInSkipCountdownS", { skipCountdown })}
          </div>
        )}
      </div>

      {/* Click overlay for click-through — fills the letterboxed space around
          the video (which already has its own onClick) so the whole screen
          is a valid click-through target, not just the video frame itself.
          Was z-index:-1, which put it in a negative stacking context instead
          of just "behind its default-stacked siblings" — some mobile touch
          engines resolve tap hit-testing against a negative-z-index element
          inconsistently with how desktop click hit-testing resolves it,
          reported live as the Skip button being untappable on mobile even
          though it visibly renders on top. Controls above is now explicitly
          z-10 instead, so ordinary (non-negative) stacking already puts it
          above this without relying on a negative value anywhere. */}
      {vast?.tracking.clickThrough && (
        <div
          className="absolute inset-0 z-0 cursor-pointer"
          onClick={handleClick}
        />
      )}
    </div>
  )
}
